import type { GoalHistoryPoint, GoalResponse } from '@/api/goals'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatDate, formatShortMonth } from '@/lib/formatDate'

function monthIndex(month: string): number {
  const [y, m] = month.split('-').map(Number)
  return y * 12 + (m - 1)
}

/** Month-end amounts since the goal started, the target as a dashed line, and a dotted projection at the current pace. */
export function GoalProgressChart({
  goal,
  points,
}: {
  goal: GoalResponse
  points: GoalHistoryPoint[]
}) {
  const { formatCompact, formatWhole } = useFormatCurrency()
  if (points.length === 0) return null

  const first = monthIndex(points[0].month)
  const last = monthIndex(points[points.length - 1].month)
  const projected = goal.projectedDate ? monthIndex(goal.projectedDate.slice(0, 7)) : null
  const due = goal.targetDate ? monthIndex(goal.targetDate.slice(0, 7)) : null
  // Leave room for the projection and the target date, but not years of empty chart.
  const end = Math.min(Math.max(last, projected ?? last, due ?? last), last + 24)
  const span = Math.max(end - first, 1)
  const top = Math.max(goal.targetAmount, ...points.map((p) => p.total)) || 1

  const x = (index: number) => ((index - first) / span) * 100
  const y = (amount: number) => 8 + (1 - Math.max(amount, 0) / top) * 88
  const line = points.map((p) => `${x(monthIndex(p.month))},${y(p.total)}`).join(' ')
  const now = points[points.length - 1]
  const showProjection = projected != null && projected <= end && goal.paceStatus !== 'REACHED'

  let caption: string
  if (goal.status !== 'ACTIVE')
    caption = `${formatWhole(now.total)} at the end of ${formatShortMonth(now.month)}`
  else if (goal.paceStatus === 'REACHED') caption = 'Target reached.'
  else if (goal.projectedDate) {
    caption = `Dotted line: at your 3-month pace you'll reach the target around ${formatDate(goal.projectedDate)}`
    if (goal.targetDate) {
      caption += goal.projectedDate <= goal.targetDate ? ', on time.' : ', after your date.'
    } else caption += '.'
  } else caption = 'Not enough months yet to work out a pace.'

  return (
    <figure className="m-0">
      <div
        role="img"
        aria-label={`Saved ${formatWhole(now.total)} of ${formatWhole(goal.targetAmount)}, ${points.length} months shown.`}
        className="relative h-32 md:h-44"
      >
        <svg
          aria-hidden="true"
          className="absolute inset-0 h-full w-full overflow-visible"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <line
            x1={0}
            x2={100}
            y1={y(goal.targetAmount)}
            y2={y(goal.targetAmount)}
            stroke="var(--fl-hero-bar-faint)"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          />
          {due != null && due <= end && (
            <line
              x1={x(due)}
              x2={x(due)}
              y1={y(goal.targetAmount)}
              y2={96}
              stroke="var(--fl-border)"
              strokeDasharray="2 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
          <polygon points={`${x(first)},96 ${line} ${x(last)},96`} fill="var(--fl-hero-bg)" />
          <polyline
            points={line}
            fill="none"
            stroke="var(--fl-hero-bar)"
            strokeWidth={2.5}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {showProjection && (
            <polyline
              points={`${x(last)},${y(now.total)} ${x(projected!)},${y(goal.targetAmount)}`}
              fill="none"
              stroke="var(--fl-hero-fg)"
              strokeWidth={2}
              strokeDasharray="3 4"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        <span
          className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--fl-hero-fg)]"
          style={{ left: `${x(last)}%`, top: `${y(now.total)}%` }}
        />
        <span className="absolute left-0 top-0 -translate-y-1/2 text-[10.5px] text-[var(--fl-text-2)]">
          Target {formatCompact(goal.targetAmount)}
        </span>
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-[var(--fl-text-3)]">
        <span>{formatShortMonth(points[0].month)}</span>
        <span>
          {end > last && goal.targetDate && due === end
            ? formatDate(goal.targetDate)
            : formatShortMonth(
                `${Math.floor(end / 12)}-${String((end % 12) + 1).padStart(2, '0')}`
              )}
        </span>
      </div>
      <figcaption className="mt-1 text-xs text-[var(--fl-text-2)]">{caption}</figcaption>
    </figure>
  )
}
